import React, { useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'sonner';
import {
  Download, Share2, Calendar, Clock, MapPin, Building2, Sparkles,
  CheckCircle2, Link2, MessageCircle, Linkedin, Twitter,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (u) => (!u ? '' : (u.startsWith('http') ? u : `${BACKEND_URL}${u}`));

const fmtDate = (d) => {
  if (!d) return 'TBD';
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return d;
  return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtTime = (d) => {
  if (!d) return '';
  const parsed = new Date(d);
  if (isNaN(parsed.getTime()) || (typeof d === 'string' && !d.includes('T') && !d.includes(':'))) return '';
  return parsed.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

export const EventTicket = ({ event, registration }) => {
  const ticketRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const ticketId = (registration?.id || '').slice(0, 12).toUpperCase();
  const isOnline = event?.event_type !== 'offline';
  const venue = isOnline ? 'Online Event' : (event?.venue || event?.venue_address || 'Venue TBA');
  const ticketType = event?.is_paid ? 'PAID' : 'FREE';
  const status = ['paid', 'free'].includes(registration?.payment_status) ? 'Confirmed' : 'Pending';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const verifyUrl = `${origin}/events/${event?.id}/ticket/${registration?.id}`;
  const eventUrl = `${origin}/events/${event?.id}`;
  const eventTime = fmtTime(event?.event_date);

  const shareText = `I'm attending ${event?.title} on ${fmtDate(event?.event_date)}${isOnline ? ' (Online)' : ` at ${venue}`}! 🎟️`;

  const downloadTicket = async () => {
    if (!ticketRef.current) return;
    setDownloading(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(ticketRef.current, { backgroundColor: null, scale: 2, useCORS: true, logging: false, onclone: fixCloneText });
      const link = document.createElement('a');
      link.download = `EvalentX-Ticket-${ticketId}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Ticket downloaded');
    } catch (e) {
      toast.error('Could not download ticket');
    } finally {
      setDownloading(false);
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: event?.title, text: shareText, url: eventUrl });
      } catch { /* user cancelled */ }
    } else {
      setShareOpen((v) => !v);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(eventUrl);
      toast.success('Share link copied');
    } catch { toast.error('Could not copy link'); }
  };

  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${eventUrl}`)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(eventUrl)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(eventUrl)}`,
  };

  return (
    <div className="w-full max-w-md mx-auto" data-testid="event-ticket">
      {/* Ticket card */}
      <div ref={ticketRef} className="relative overflow-hidden rounded-2xl shadow-2xl"
        style={{ background: 'linear-gradient(150deg, #0A1A3F 0%, #002FA7 100%)' }}>
        {/* texture */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 20% 10%, rgba(255,255,255,0.08), transparent 40%), radial-gradient(circle at 90% 90%, rgba(255,255,255,0.06), transparent 35%)',
        }} />

        {/* cover banner */}
        {mediaUrl(event?.cover_image) && (
          <div className="h-28 w-full overflow-hidden border-b border-white/10">
            <img src={mediaUrl(event.cover_image)} alt="" crossOrigin="anonymous" className="w-full h-full object-cover opacity-80" />
          </div>
        )}

        <div className="relative p-6">
          {/* header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center rounded-md bg-white/15">
                <Sparkles size={14} className="text-white" />
              </div>
              <span className="text-white font-extrabold tracking-tight text-sm">EvalentX</span>
              <span data-export-badge="" className="text-[9px] font-bold tracking-[0.2em] uppercase text-blue-200 border border-white/20 rounded px-1.5 py-0.5">Event Pass</span>
            </div>
            <span data-export-badge="" className={`text-[9px] font-bold tracking-[0.15em] uppercase rounded-full px-2.5 py-1 ${event?.is_paid ? 'bg-amber-400/90 text-amber-950' : 'bg-emerald-400/90 text-emerald-950'}`}>{ticketType}</span>
          </div>

          {/* title */}
          <h3 className="mt-5 text-white text-2xl font-extrabold leading-tight tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{event?.title}</h3>
          <div className="mt-2 flex items-center gap-2" data-testid="ticket-organiser">
            {mediaUrl(event?.company_logo) && !logoBroken ? (
              <span className="inline-flex items-center justify-center h-8 min-w-8 max-w-[96px] rounded-md bg-white px-1.5 shrink-0">
                <img src={mediaUrl(event.company_logo)} alt={`${event?.company_name || 'Organiser'} logo`} crossOrigin="anonymous"
                  onError={() => setLogoBroken(true)} className="h-6 w-auto max-w-[84px] object-contain" data-testid="ticket-organiser-logo" />
              </span>
            ) : <Building2 size={12} className="text-blue-200" />}
            <p className="text-blue-200 text-xs"><span className="opacity-70">Hosted by</span> <span className="font-semibold text-white" data-testid="ticket-organiser-name">{event?.company_name || 'EvalentX'}</span></p>
          </div>

          {/* details */}
          <div className="mt-5 grid grid-cols-2 gap-y-4 gap-x-3">
            <Detail icon={Calendar} label="Date" value={fmtDate(event?.event_date)} />
            {eventTime && <Detail icon={Clock} label="Time" value={eventTime} />}
            <Detail icon={MapPin} label={isOnline ? 'Mode' : 'Venue'} value={venue} />
            <Detail icon={CheckCircle2} label="Status" value={status} valueClass={status === 'Confirmed' ? 'text-emerald-300' : 'text-amber-300'} />
          </div>

          {/* stub */}
          <div className="relative mt-6 pt-5 border-t border-dashed border-white/25">
            <div className="absolute -left-6 -top-3 w-6 h-6 rounded-full" style={{ background: '#F4F4F5' }} />
            <div className="absolute -right-6 -top-3 w-6 h-6 rounded-full" style={{ background: '#F4F4F5' }} />
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-blue-200">Attendee</p>
                <p className="text-white font-bold text-base truncate">{registration?.name || 'Guest'}</p>
                <p className="mt-2 text-[10px] font-bold tracking-[0.15em] uppercase text-blue-200">Ticket ID</p>
                <p className="text-white font-mono font-semibold text-sm">{ticketId}</p>
              </div>
              <div className="shrink-0 bg-white p-2 rounded-lg">
                <QRCodeCanvas value={verifyUrl} size={76} level="M" includeMargin={false} />
                <p className="text-[8px] text-center text-slate-500 mt-1 font-semibold">SCAN TO VERIFY</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-5 flex flex-wrap gap-2">
        <button onClick={downloadTicket} disabled={downloading} data-testid="download-ticket-btn"
          className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white py-3 rounded-lg font-semibold text-sm transition-colors disabled:opacity-60">
          <Download size={16} /> {downloading ? 'Preparing…' : 'Download Ticket'}
        </button>
        <button onClick={nativeShare} data-testid="share-ticket-btn"
          className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5 py-3 rounded-lg font-semibold text-sm transition-colors">
          <Share2 size={16} /> Share Ticket
        </button>
      </div>

      {/* Share options */}
      {shareOpen && (
        <div className="mt-3 grid grid-cols-4 gap-2" data-testid="share-options">
          <ShareBtn onClick={() => window.open(shareLinks.whatsapp, '_blank')} icon={MessageCircle} label="WhatsApp" color="#25D366" testid="share-whatsapp" />
          <ShareBtn onClick={() => window.open(shareLinks.linkedin, '_blank')} icon={Linkedin} label="LinkedIn" color="#0A66C2" testid="share-linkedin" />
          <ShareBtn onClick={() => window.open(shareLinks.twitter, '_blank')} icon={Twitter} label="X" color="#0A0A0A" testid="share-twitter" />
          <ShareBtn onClick={copyLink} icon={Link2} label="Copy" color="#52525B" testid="share-copy" />
        </div>
      )}
    </div>
  );
};

// html2canvas draws glyphs lower than the browser (Tailwind img display:block quirk); give clipped text room in the export copy only.
const fixCloneText = (doc) => {
  const style = doc.createElement('style');
  style.textContent = 'img{display:inline-block}.truncate{line-height:1.6!important;padding-bottom:4px}[data-export-badge]{line-height:1!important;padding-top:1px!important;padding-bottom:7px!important}';
  doc.head.appendChild(style);
};

const Detail = ({ icon: Icon, label, value, valueClass = 'text-white' }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-blue-200 flex items-center gap-1"><Icon size={11} /> {label}</p>
    <p className={`font-semibold text-sm mt-0.5 truncate ${valueClass}`}>{value}</p>
  </div>
);

const ShareBtn = ({ onClick, icon: Icon, label, color, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="flex flex-col items-center gap-1.5 py-2.5 rounded-lg border border-[#E4E4E7] hover:bg-gray-50 transition-colors">
    <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: `${color}1A` }}>
      <Icon size={16} style={{ color }} />
    </span>
    <span className="text-[10px] font-semibold text-[#52525B]">{label}</span>
  </button>
);

export default EventTicket;
